import { getAxiosError } from "../../../../../utils/axiosError";
import { type AppDispatch } from "../../../../Application/globaltypes/redux";
import { type BasePerformanceRequestFilterParams, type FlowGoalsFilterParams } from "../../../Common/models";
import { sendExportRequest } from "../../../Common/utils/performanceUtils";
import DataService from "../../services/flowsDataService";
import {
  setEngagementData,
  reqEngagementData,
  setEngagementError,
  reqDateRanges,
  setDateRanges,
  setDateRangesError,
  reqPeopleDetails,
  setPeopleDetails,
  setPeopleDetailsError,
  reqGoalLine,
  reqGoalCards,
  reqGoalTotals,
  setGoalLine,
  setGoalLineError,
  setGoalTotals,
  setGoalTotalsError,
  setGoalCardsError,
  setGoalCards,
  isAllowedGoal,
  type FlowGoalFilterParams,
} from "../slices/flowPerformanceSlice";

type PackedGoalInfo = {
  applicationLabel: string | undefined;
  measureLabel: string | undefined;
};

const GOAL_NAME_LOOKUP: Record<string, FlowGoalFilterParams[]> = {
  "Teams All Activity": [
    { application: "Teams", activity: "Meetings" },
    { application: "Teams", activity: "Messages" },
    { application: "Teams", activity: "Calls" },
  ],
  "OneDrive All Activity": [
    { application: "OneDrive", activity: "Files Shared" },
    { application: "OneDrive", activity: "Files Synced" },
    { application: "OneDrive", activity: "Files Viewed" },
  ],
  "SharePoint All Activity": [
    { application: "SharePoint", activity: "Files Synced" },
    { application: "SharePoint", activity: "Files Viewed" },
    { application: "SharePoint", activity: "Page Visits" },
    { application: "SharePoint", activity: "Shared Files" },
  ],
  "Copilot All Activity": [{ application: "Copilot", activity: "All Activity" }],
};

const getGoalNames = (goalInfo: PackedGoalInfo): FlowGoalFilterParams[] => {
  if (Object.values(goalInfo).some((v) => v === undefined)) {
    return [];
  }

  if (goalInfo.measureLabel === "All Activity") {
    return GOAL_NAME_LOOKUP[`${goalInfo.applicationLabel} All Activity`];
  }

  return [{ application: goalInfo.applicationLabel!, activity: goalInfo.measureLabel! }];
};

export const fetchEngagementData = (flowId: number, filterParams: BasePerformanceRequestFilterParams = {}) => {
  return async (dispatch: AppDispatch) => {
    dispatch(reqEngagementData());
    try {
      const { data } = await DataService.getFlowEngagement(flowId, filterParams);
      if (data.Relations === undefined) {
        throw new Error(
          "Failed to fetch information for flow engagement. We're working to fix this issue. Please check back in later.",
        );
      }
      dispatch(
        setEngagementData({
          data,
        }),
      );
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(
          setEngagementError({
            errorMessage,
          }),
        );
      }
    }
  };
};

export const fetchFlowDateRanges = (flowId: number) => {
  return async (dispatch: AppDispatch) => {
    dispatch(reqDateRanges());
    try {
      const { data } = await DataService.getDateRanges(flowId);
      dispatch(setDateRanges({ data }));
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setDateRangesError({ errorMessage }));
      }
    }
  };
};

export const fetchPeopleDetails = (flowId: number, filterParams: BasePerformanceRequestFilterParams = {}) => {
  return async (dispatch: AppDispatch) => {
    dispatch(reqPeopleDetails());
    try {
      const result = (await DataService.getFlowPeopleDetails(flowId, filterParams)).data;

      const resultsWithId = result.map((item, index) => {
        const id = `${item.UserId}${index}`;
        return {
          ...item,
          id,
        };
      });
      dispatch(setPeopleDetails({ data: resultsWithId }));
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setPeopleDetailsError({ errorMessage }));
      }
    }
  };
};

export const fetchGoalsLineChart = (flowId: number, filterParams: FlowGoalsFilterParams, goal: PackedGoalInfo) => {
  return async (dispatch: AppDispatch) => {
    if (!isAllowedGoal(goal)) {
      return;
    }
    dispatch(reqGoalLine());
    const goalNames = getGoalNames(goal);

    try {
      const res = await Promise.all(
        goalNames.map((goalName) => DataService.getFlowGoalLineChart(flowId, filterParams, goalName)),
      );
      dispatch(setGoalLine(res.map((r) => r.data || [])));
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setGoalLineError(errorMessage));
      }
    }
  };
};

export const fetchGoalsTotals = (flowId: number, filterParams: FlowGoalsFilterParams, goal: PackedGoalInfo) => {
  return async (dispatch: AppDispatch) => {
    if (!isAllowedGoal(goal)) {
      return;
    }
    dispatch(reqGoalTotals());
    const goalNames = getGoalNames(goal);
    try {
      const res = await Promise.all(
        goalNames.map((goalName) => DataService.getFlowGoalTotals(flowId, filterParams, goalName)),
      );
      dispatch(setGoalTotals(res.map((r) => r.data)));
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setGoalTotalsError(errorMessage));
      }
    }
  };
};

export const fetchGoalsCards = (flowId: number, filterParams: FlowGoalsFilterParams, goal: PackedGoalInfo) => {
  return async (dispatch: AppDispatch) => {
    if (!isAllowedGoal(goal) || goal.applicationLabel === "Copilot") {
      return;
    }
    dispatch(reqGoalCards());
    const goalNames = getGoalNames(goal);
    try {
      const res = await Promise.all(
        goalNames.map((goalName) => DataService.getFlowGoalCards(flowId, filterParams, goalName)),
      );
      dispatch(setGoalCards(res.map((r) => r.data)));
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setGoalCardsError(errorMessage));
      }
    }
  };
};

export const handleFlowExport = (flowId: number, dateFilter: BasePerformanceRequestFilterParams, flowTitle: string) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) => DataService.getFlowExport(flowId, dateFilter),
    flowTitle,
    abortController,
  );
};

export const setDefaultValues = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(
      setEngagementData({
        data: {
          FlowId: -1,
          AccountId: -1,
          Relations: [],
        },
      }),
    );
    dispatch(
      setPeopleDetails({
        data: [],
      }),
    );
  };
};
